import { createSlice, createAsyncThunk, createEntityAdapter, PayloadAction } from '@reduxjs/toolkit';
import { collection, query, where, onSnapshot, orderBy, Timestamp, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, Unsubscribe } from 'firebase/firestore';
import { db as firestore } from '@/lib/firebase'; // Corrected path
import type { RootState, AppDispatch } from '../index';
import type { Review as FirestoreReview } from '@/types/firestore';

// Define a serializable review type for Redux state
export interface ReduxReview extends Omit<FirestoreReview, 'date' | 'id'> {
  id: string; // Make id non-optional
  date: number; // Store date as a serialized number (milliseconds)
  // username is already part of FirestoreReview
}

// Type for the ID, must match ReduxReview['id']
// type ReviewId = string;

const reviewsAdapter = createEntityAdapter<ReduxReview>({
  // selectId: (review: ReduxReview): ReviewId => review.id, // Using specific type for ID if needed
  // No selectId needed if the ID property is named 'id'
  sortComparer: (a, b) => b.date - a.date, // Default sort by date descending
});

interface ActiveDrinkState extends ReturnType<typeof reviewsAdapter.getInitialState> {
  drinkId: string | null;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  listenersActive: boolean; // To track if listeners are currently active
}

// Module-level variable to store unsubscribe functions for listeners
let activeReviewListeners: Unsubscribe[] = [];

const initialState: ActiveDrinkState = reviewsAdapter.getInitialState({
  drinkId: null,
  loading: 'idle',
  error: null,
  listenersActive: false,
});

// Helper to unsubscribe all active listeners
const unsubscribeAllActiveReviewListeners = () => {
  activeReviewListeners.forEach(unsub => unsub());
  activeReviewListeners = [];
};

// Async thunk to set up Firestore listeners for a specific drink's reviews
export const setupReviewsListenerForActiveDrink = createAsyncThunk<
  void, // This thunk primarily manages side effects (listeners)
  { drinkId: string; currentUserId?: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'activeDrink/setupReviewsListener',
  async ({ drinkId, currentUserId }, { dispatch, rejectWithValue, getState }) => {
    unsubscribeAllActiveReviewListeners(); // Unsubscribe from any previous listeners
    dispatch(activeDrinkSlice.actions.setLoading('pending'));
    dispatch(activeDrinkSlice.actions.setDrinkId(drinkId)); // Set current drinkId

    // Local arrays to hold data from listeners before merging
    let localPublicReviews: ReduxReview[] = [];
    let localUserReviews: ReduxReview[] = [];

    try {
      const reviewsCol = collection(firestore, 'reviews');
      let initialPublicLoadComplete = false;
      let initialUserLoadComplete = !currentUserId; // If no user, consider user load complete

      const processAndDispatchMergedReviews = () => {
        const reviewMap = new Map<string, ReduxReview>();
        // Add user reviews first (they take precedence)
        localUserReviews.forEach(review => reviewMap.set(review.id, review));
        // Add public reviews, but don't overwrite if a user's version already exists
        localPublicReviews.forEach(review => {
          if (!reviewMap.has(review.id)) {
            reviewMap.set(review.id, review);
          }
        });
        const mergedReviews = Array.from(reviewMap.values());
        mergedReviews.sort((a,b) => b.date - a.date); // Ensure final sort
        dispatch(activeDrinkSlice.actions.setReviewsFromListener(mergedReviews));

        if (initialPublicLoadComplete && initialUserLoadComplete && getState().activeDrink.loading === 'pending') {
          dispatch(activeDrinkSlice.actions.setLoading('succeeded'));
        }
      };

      // 1. Listener for public reviews
      const publicQuery = query(
        reviewsCol,
        where('drinkId', '==', drinkId),
        where('isPublic', '==', true),
        orderBy('date', 'desc')
      );
      const unsubPublic = onSnapshot(publicQuery, (snapshot) => {
        localPublicReviews = snapshot.docs.map(doc => ({
          ...(doc.data() as FirestoreReview),
          id: doc.id,
          date: (doc.data().date as Timestamp).toMillis(),
        } as ReduxReview));
        initialPublicLoadComplete = true;
        processAndDispatchMergedReviews();
      }, (error) => {
        console.error("Error in public reviews listener:", error);
        dispatch(activeDrinkSlice.actions.setError(error.message || 'Public reviews listener failed'));
      });
      activeReviewListeners.push(unsubPublic);

      // 2. Listener for current user's reviews (if userId provided)
      if (currentUserId) {
        const userReviewQuery = query(
          reviewsCol,
          where('drinkId', '==', drinkId),
          where('userId', '==', currentUserId)
        );
        const unsubUser = onSnapshot(userReviewQuery, (snapshot) => {
          localUserReviews = snapshot.docs.map(doc => ({
            ...(doc.data() as FirestoreReview),
            id: doc.id,
            date: (doc.data().date as Timestamp).toMillis(),
          } as ReduxReview));
          initialUserLoadComplete = true;
          processAndDispatchMergedReviews();
        }, (error) => {
          console.error("Error in user reviews listener:", error);
          dispatch(activeDrinkSlice.actions.setError(error.message || 'User reviews listener failed'));
        });
        activeReviewListeners.push(unsubUser);
      } else {
        // If no currentUserId, public load complete is enough to mark success if it was the only one pending
        if (initialPublicLoadComplete && getState().activeDrink.loading === 'pending') {
             dispatch(activeDrinkSlice.actions.setLoading('succeeded'));
        }
      }
      dispatch(activeDrinkSlice.actions.setListenersActive(true));

    } catch (error: any) {
      console.error("Error setting up reviews listener:", error);
      dispatch(activeDrinkSlice.actions.setListenersActive(false));
      unsubscribeAllActiveReviewListeners();
      return rejectWithValue(error.message || 'Failed to setup reviews listener');
    }
  }
);

interface NewReviewData {
  drinkId: string;
  drinkName: string; // Denormalized, from the drink object in modal
  brandId: string; // Denormalized, from the drink object
  brandName: string; // Denormalized, from the drink object
  userId: string;
  username: string; // Denormalized, from authenticated user
  rating: number;
  reviewText: string;
  isPublic: boolean;
}

export const addReviewForActiveDrink = createAsyncThunk<
  ReduxReview, // Return type: the newly created review, serialized for Redux
  NewReviewData,
  { state: RootState; rejectValue: string }
>(
  'activeDrink/addReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      const newReviewDoc: Omit<FirestoreReview, 'id'> = {
        ...reviewData,
        date: serverTimestamp() as Timestamp, // Use serverTimestamp for consistency
      };
      const docRef = await addDoc(collection(firestore, 'reviews'), newReviewDoc);
      
      // Construct the ReduxReview object to return and add to state
      // We need to simulate the serverTimestamp for the optimistic update if we don't re-fetch
      // Or, better, re-fetch the single review or all reviews. For now, let's assume immediate availability for optimistic update.
      const createdReview: ReduxReview = {
        ...reviewData,
        id: docRef.id,
        date: Date.now(), // Use current client time for optimistic update, Firestore will have server time
      };
      return createdReview;
    } catch (error: any) {
      console.error("Error adding review:", error);
      return rejectWithValue(error.message || 'Failed to add review');
    }
  }
);

export interface UpdateReviewData {
  reviewId: string;
  // Allow updating only specific fields relevant to user edits
  changes: {
    rating?: number;
    reviewText?: string;
    isPublic?: boolean;
    // date will be updated with serverTimestamp
  };
}

export const updateReviewForActiveDrink = createAsyncThunk<
  { reviewId: string; changes: Partial<ReduxReview> }, // Return the ID and updated (serialized) changes for the reducer
  UpdateReviewData,
  { state: RootState; rejectValue: string }
>(
  'activeDrink/updateReview',
  async ({ reviewId, changes }, { rejectWithValue }) => {
    try {
      const reviewRef = doc(firestore, 'reviews', reviewId);
      const updatePayload: Partial<FirestoreReview> = {
        ...changes,
        date: serverTimestamp() as Timestamp, // Always update the date on edit
      };
      await updateDoc(reviewRef, updatePayload);

      // For the optimistic update in Redux, serialize the changes
      const serializedChanges: Partial<ReduxReview> = { ...changes };
      if (updatePayload.date) { // If date was part of payload (it always is here)
        serializedChanges.date = Date.now(); // Use client time for optimistic update
      }

      return { reviewId, changes: serializedChanges };
    } catch (error: any) {
      console.error("Error updating review:", error);
      return rejectWithValue(error.message || 'Failed to update review');
    }
  }
);

export const deleteReviewForActiveDrink = createAsyncThunk<
  string, // Return type: the ID of the deleted review
  string, // Argument: reviewId
  { state: RootState; rejectValue: string }
>(
  'activeDrink/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      const reviewRef = doc(firestore, 'reviews', reviewId);
      await deleteDoc(reviewRef);
      return reviewId;
    } catch (error: any) {
      console.error("Error deleting review:", error);
      return rejectWithValue(error.message || 'Failed to delete review');
    }
  }
);

const activeDrinkSlice = createSlice({
  name: 'activeDrink',
  initialState,
  reducers: {
    setDrinkId: (state, action: PayloadAction<string | null>) => {
      state.drinkId = action.payload;
    },
    setLoading: (state, action: PayloadAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setListenersActive: (state, action: PayloadAction<boolean>) => {
      state.listenersActive = action.payload;
    },
    setReviewsFromListener: (state, action: PayloadAction<ReduxReview[]>) => {
      // Now this reducer simply takes the merged list and sets it
      reviewsAdapter.setAll(state, action.payload);
    },
    clearActiveDrinkData: (state) => {
      unsubscribeAllActiveReviewListeners();
      reviewsAdapter.removeAll(state);
      state.drinkId = null;
      state.loading = 'idle';
      state.error = null;
      state.listenersActive = false;
    },
    reviewAdded: (state, action: PayloadAction<ReduxReview>) => {
      reviewsAdapter.upsertOne(state, action.payload);
      // Sorting handled by adapter's sortComparer or by setAll in listener update
    },
    reviewUpdated: (state, action: PayloadAction<{id: string, changes: Partial<ReduxReview>}>) => {
      reviewsAdapter.updateOne(state, action.payload);
    },
    reviewDeleted: (state, action: PayloadAction<string>) => {
      reviewsAdapter.removeOne(state, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupReviewsListenerForActiveDrink.pending, () => {
        // State updated by direct dispatches within thunk
      })
      .addCase(setupReviewsListenerForActiveDrink.fulfilled, (state) => {
        // Listener setup successful, loading state managed within thunk based on snapshot receipt
        // state.loading = 'succeeded'; // Can be set here if not managed by initial snapshot logic
        state.listenersActive = true;
      })
      .addCase(setupReviewsListenerForActiveDrink.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload as string;
        state.listenersActive = false;
      })
      .addCase(addReviewForActiveDrink.pending, (state) => {
        // Optionally set a specific loading state for adding a review if needed
        state.loading = 'pending'; // Or a more specific loading flag e.g., state.addReviewLoading = 'pending'
      })
      .addCase(addReviewForActiveDrink.fulfilled, (state, action) => {
        // The reviewAdded reducer will handle adding it to the adapter
        // No direct state manipulation here, rely on the reducer for consistency
        // OR directly call reviewsAdapter.addOne(state, action.payload) if not using the reviewAdded reducer for this thunk.
        // For now, we will dispatch reviewAdded separately or let it be handled if the thunk dispatches it.
        // Let's simplify: directly add here and ensure reviewAdded reducer can also be used by optimistic updates.
        reviewsAdapter.upsertOne(state, action.payload); // Add/update the review
         // Re-sort to ensure the new review is in the correct place
        const allReviews = reviewsAdapter.getSelectors().selectAll(state);
        allReviews.sort((a, b) => b.date - a.date);
        reviewsAdapter.setAll(state, allReviews);
        state.loading = 'succeeded'; // Reset general loading or specific addReviewLoading
      })
      .addCase(addReviewForActiveDrink.rejected, (state, action) => {
        state.error = action.payload as string; // Set error for adding review
        state.loading = 'failed'; // Reset general loading or specific addReviewLoading
      })
      .addCase(updateReviewForActiveDrink.pending, (state) => {
        // Optionally set a specific loading state for updating a review
        state.loading = 'pending';
      })
      .addCase(updateReviewForActiveDrink.fulfilled, (state, action) => {
        reviewsAdapter.updateOne(state, {id: action.payload.reviewId, changes: action.payload.changes});
        // Re-sort to ensure the updated review is in the correct place if date changed
        const allReviews = reviewsAdapter.getSelectors().selectAll(state);
        allReviews.sort((a, b) => b.date - a.date);
        reviewsAdapter.setAll(state, allReviews);
        state.loading = 'succeeded';
      })
      .addCase(updateReviewForActiveDrink.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = 'failed';
      })
      .addCase(deleteReviewForActiveDrink.pending, (state) => {
        // Optionally set a specific loading state for deleting a review
        state.loading = 'pending';
      })
      .addCase(deleteReviewForActiveDrink.fulfilled, (state, action) => {
        reviewsAdapter.removeOne(state, action.payload); // action.payload is reviewId
        state.loading = 'succeeded';
      })
      .addCase(deleteReviewForActiveDrink.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = 'failed';
      });
  },
});

export const {
  setDrinkId,
  setLoading,
  setError,
  setListenersActive,
  setReviewsFromListener,
  clearActiveDrinkData,
  reviewAdded,
  reviewUpdated,
  reviewDeleted
} = activeDrinkSlice.actions;

// Selectors from the adapter
export const {
  selectAll: selectAllActiveDrinkReviews,
  selectById: selectActiveDrinkReviewById,
  selectIds: selectActiveDrinkReviewIds,
} = reviewsAdapter.getSelectors((state: RootState) => state.activeDrink);

// Other selectors
export const selectActiveDrinkId = (state: RootState): string | null => state.activeDrink.drinkId;
export const selectActiveDrinkLoadingStatus = (state: RootState): 'idle' | 'pending' | 'succeeded' | 'failed' => state.activeDrink.loading;
export const selectActiveDrinkError = (state: RootState): string | null => state.activeDrink.error;
export const selectActiveDrinkListenersActive = (state: RootState): boolean => state.activeDrink.listenersActive;

export default activeDrinkSlice.reducer; 